
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import SunEditor from "suneditor-react";
import ImageManagerModal from "./admin/ImageManagerModal";

const TextEditor = forwardRef(
  (
    {
      value = "",
      onChange,
      onFocus,
      name,
      canUpdate = true,
      importCustomInsertImage = false,
      height = "300px",
    },
    ref
  ) => {
    const [content, setContent] = useState(value);
    const editorRef = useRef(null);
    const [selectedMultiMedia, setSelectedMultiMedia] = useState([]);
    const [isModalImageManagerOpen, setIsModalImageManagerOpen] =
      useState(false);

    useEffect(() => {
      setContent(value);
    }, [value]);

    useImperativeHandle(ref, () => ({
      insertHTML: (html) => {
        if (!editorRef.current) {
          return;
        }

        editorRef.current.insertHTML(html);
        editorRef.current.core.context.element.wysiwyg.focus();
      },
      focus: () => {
        editorRef.current?.core.context.element.wysiwyg.focus();
      },
      getContents: () => {
        return editorRef.current?.getContents() || "";
      },
    }));

    const handleChange = (editorContent) => {
      setContent(editorContent);

      if (onChange) {
        onChange({ name, value: editorContent });
      }
    };

    const handleCloseImageManagerModal = () => {
      setSelectedMultiMedia([]);
      setIsModalImageManagerOpen(false);
    };

    const handleOpenImageManagerModal = () => {
      setIsModalImageManagerOpen(true);
    };

    const handlePressImage = (media) => {
      if (selectedMultiMedia.map((item) => item.id).includes(media.id)) {
        setSelectedMultiMedia((currentMedia) => [
          ...currentMedia.filter((item) => item.id != media.id),
        ]);
      } else {
        setSelectedMultiMedia((currentMedia) => [
          media,
          ...currentMedia,
        ]);
      }
    };

    const insertMultiImage = () => {
      selectedMultiMedia.forEach((image) => {
        if (image.url) {
          const imageHTML = `<img src="${image.url}" alt="inserted image" />`;
          editorRef.current?.insertHTML(imageHTML);
        }
      });
      setSelectedMultiMedia([]);
      handleCloseImageManagerModal();
    };

    const insertImageFormUrl = (imageUrl) => {
      if (imageUrl) {
        const imageHTML = `<img src="${imageUrl}" alt="inserted image" />`;
        editorRef.current?.insertHTML(imageHTML);
      }
    };

    const getSunEditorInstance = (sunEditor) => {
      editorRef.current = sunEditor;

      setTimeout(() => {
        const editorDom =
          editorRef.current.core.context.element.wysiwyg;
        editorDom.setAttribute(
          "contenteditable",
          canUpdate ? "true" : "false"
        );

        const toolbar =
          editorRef.current.core.context.element.toolbar;
        if (!toolbar) return;

        if (!canUpdate) {
          toolbar.classList.add("pointer-events-none");
        }

        const imageButton = toolbar.querySelector(
          'button[data-command="image"][aria-label="Image"]'
        );

        if (imageButton) {
          imageButton.onclick = (event) => {
            event.preventDefault();
            event.stopPropagation();
            handleOpenImageManagerModal();
          };
        }
      }, 200);
    };

    const buttonList = [
      ["undo", "redo"],
      ["font", "fontSize", "formatBlock"],
      [
        "bold",
        "underline",
        "italic",
        "strike",
        "subscript",
        "superscript",
      ],
      ["removeFormat"],
      "/",
      ["fontColor", "hiliteColor"],
      ["outdent", "indent"],
      ["align", "horizontalRule", "list", "table"],
      importCustomInsertImage
        ? ["link", "image", "video"]
        : ["link", "video"],
      ["fullScreen", "showBlocks", "codeView"],
      ["preview", "print"],
    ];

    return (
      <div>
        <div className="editor-wrapper z-[9999]">
          <SunEditor
            getSunEditorInstance={getSunEditorInstance}
            setContents={content}
            onChange={handleChange}
            onFocus={onFocus}
            setOptions={{
              buttonList,
              height,
              placeholder: "Nhập nội dung...",
              disable: !canUpdate,
            }}
          />
        </div>

        {isModalImageManagerOpen && (
          <ImageManagerModal
            onClose={handleCloseImageManagerModal}
            onPress={handlePressImage}
            multiImage={true}
            selectedMultiMedia={selectedMultiMedia}
            onInsertImage={insertMultiImage}
            onInsertImageFormUrl={insertImageFormUrl}
          />
        )}
      </div>
    );
  }
);

TextEditor.displayName = "TextEditor";

export default TextEditor;

